import { useQuery } from '@tanstack/react-query';

import { useRequest, type QueryFunction } from '@frontend/api';

import type {
  CostingVariablesUnits,
  SheetMetalCostingVariables,
} from './types';

export type UseGetCostingVariablesDataParams = {
  interrogationFileId: number | string;
  units: CostingVariablesUnits;
  /**
   * Not sent to the API. The weight is derived from the part's material, so it
   * is part of the key to refetch when the active material changes.
   */
  materialId?: number | null;
};

export type CostingVariablesResponse = {
  /**
   * Null unless the interrogation file is a sheet metal task at or above the
   * base feature schema version.
   */
  sheetMetal: SheetMetalCostingVariables | null;
};

export const getUseGetCostingVariablesDataKey = ({
  interrogationFileId,
  units,
  materialId,
}: UseGetCostingVariablesDataParams) =>
  ['costingVariablesData', interrogationFileId, units, materialId] as const;

export const useGetCostingVariablesData: QueryFunction<
  CostingVariablesResponse,
  [UseGetCostingVariablesDataParams],
  ReturnType<typeof getUseGetCostingVariablesDataKey>
> = (
  { interrogationFileId, units, materialId },
  { queryOptions, requestOptions } = {},
) => {
  const request = useRequest(requestOptions);
  const hasInterrogationFileId =
    interrogationFileId !== '' && interrogationFileId != null;

  return useQuery({
    ...queryOptions,
    queryKey: getUseGetCostingVariablesDataKey({
      interrogationFileId,
      units,
      materialId,
    }),
    enabled: hasInterrogationFileId && (queryOptions?.enabled ?? true),
    queryFn: ({ signal }) =>
      request<CostingVariablesResponse>(
        `/parts/interrogation_files/${interrogationFileId}/costing_variables`,
        {
          query: { units },
          init: { signal },
        },
      ),
  });
};
